import { ImportCurve } from 'iconsax-react';
import { lazy, type PropsWithChildren, Suspense, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useBoolean } from 'usehooks-ts';
import ShareArrow from '@/assets/images/icons/share-arrow.svg?react';
import { BrandLogo } from '@/components/common/brand-logo';
import { SafeImage } from '@/components/common/safe-image';
import LoadingLayout from '@/components/layouts/progress-layout/loading-layout';
import useUploadFile from '@/hooks/files/use-upload-file';
import useAsyncValue from '@/hooks/use-async-value';
import useCaptureScreenshot from '@/hooks/use-capture-screenshot';
import useDownloadUrl from '@/hooks/use-download-url';
import useUserPlatform from '@/hooks/use-user-platform';
import cn from '@/utils/merge-class-names';
import { convertBlobToUploadable } from '@/utils/uploadable';

interface Props {
  data: string | undefined;
  avatarUrl?: string;
  subtitle?: string;
  label?: string;
  className?: string;
  onForward?: (url: string) => void;
  onCaptured?: (blob: Blob) => void;
}

const QRCode = lazy(() => import('react-qr-code'));

export default function QrCodeView({
  data,
  avatarUrl,
  subtitle,
  label = '',
  className,
  onForward,
  children,
  onCaptured,
}: PropsWithChildren<Props>) {
  const { t } = useTranslation();
  const { isEmbedded } = useUserPlatform();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { value: isDownloading, setTrue: setDownloading, setFalse: setNotDownloading } = useBoolean(false);
  const { value: isForwarding, setTrue: setForwarding, setFalse: setNotForwarding } = useBoolean(false);
  const { handleDownloadUrl } = useDownloadUrl();
  const { upload } = useUploadFile();
  const { capture: handleCaptureImage } = useCaptureScreenshot();

  const qrcodeImage = useAsyncValue(() => handleCaptureImage(containerRef.current));

  useEffect(() => {
    if (qrcodeImage) {
      onCaptured?.(qrcodeImage);
    }
  }, [qrcodeImage]);

  const handleDownload = async () => {
    try {
      setDownloading();
      const url = URL.createObjectURL(qrcodeImage ?? (await handleCaptureImage(containerRef.current)));

      if (url) {
        await handleDownloadUrl(url, 'pic');
      }

      return url;
    } catch (err) {
      console.error(err);
      return Promise.reject();
    } finally {
      setNotDownloading();
    }
  };

  const handleForward = async () => {
    try {
      setForwarding();
      const url = await upload(
        convertBlobToUploadable(
          qrcodeImage ?? (await handleCaptureImage(containerRef.current)),
          `qr-code-${label}.png`,
        ),
      );
      onForward?.(url);
    } catch (err) {
      console.error(err);
      return Promise.reject();
    } finally {
      setNotForwarding();
    }
  };

  return (
    <div className={cn('flex h-full flex-1 gap-6 flex-col group-[.modal-layout]:gap-4', className)}>
      <div className='flex flex-1 flex-col flex-center'>
        <div
          ref={containerRef}
          className='flex flex-col items-stretch py-6 px-3 select-none rounded-lg bg-neutral-0'>
          <div className='flex items-center gap-4'>
            {avatarUrl && (
              <SafeImage
                displayType='img'
                src={avatarUrl}
                className='aspect-square size-10 rounded-full bg-base-100 object-cover'
                size={[40, 40]}
              />
            )}

            <div className='flex flex-col gap-1 flex-1 w-0'>
              <h6 className='font-bold text-body1 truncate'>{label}</h6>

              {subtitle && <span className='text-body3 text-secondary font-mono'>{subtitle}</span>}
            </div>

            {!isEmbedded && (
              <BrandLogo
                variant='full'
                as='image'
                className='size-10 bg-base-100 rounded-lg'
              />
            )}
          </div>

          {data ? (
            <Suspense fallback={<LoadingLayout className='aspect-square w-full' />}>
              <QRCode
                className='w-64 p-4 rounded-none'
                level='M'
                value={data}
                size={256}
              />
            </Suspense>
          ) : (
            <LoadingLayout className='aspect-square w-full' />
          )}
        </div>

        {children}
      </div>

      <div className='flex justify-evenly group-[.modal-layout]:py-0'>
        <button
          type='button'
          disabled={isDownloading}
          onClick={handleDownload}
          className='btn btn-square btn-ghost basis-24 h-max group-[.modal-layout]:size-24'>
          <span className='flex flex-col gap-2 items-center'>
            {isDownloading ? (
              <span className='loading loading-spinner' />
            ) : (
              <ImportCurve
                variant='Outline'
                size={24}
              />
            )}
            <span className='desktop:text-h3 text-body1'> {t('common.save')}</span>
          </span>
        </button>

        <button
          type='button'
          disabled={isForwarding}
          onClick={handleForward}
          className='btn btn-square btn-ghost basis-24  group-[.modal-layout]:size-24 h-max'>
          <span className='flex flex-col gap-2 items-center'>
            {isForwarding ? <span className='loading loading-spinner' /> : <ShareArrow className='size-6' />}
            <span className='desktop:text-h3 text-body1'> {t('conversation.setting.group.qr-code.share-image')}</span>
          </span>
        </button>
      </div>
    </div>
  );
}
